import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../../hooks/useStore';
import { DataViewEditBox, useForm } from '../../../../../lib/components';
import { PaymentDataAddressForm } from './form';
import { PaymentDataAddressView } from './view';
import { Address } from '@pxtra-client-lib-legacy/gen';

export const CompanyPaymentAddressData = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const form = useForm();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [addressData, setAddressData] = useState<Address | undefined>(undefined);
  const [isEqual, setIsEqual] = useState<boolean | undefined>(undefined);

  const invoiceAddress = companyStore.paymentData?.invoice_address;
  const generalAddress = companyStore.generalData?.address;

  useEffect(() => {
    if (invoiceAddress && generalAddress) {
      const isEmpty = Object.values(invoiceAddress).every((x) => x === null || x === '');
      setIsEqual(isEmpty);
      if (isEmpty) {
        setAddressData(generalAddress);
      } else {
        setAddressData(invoiceAddress);
      }
    }
  }, [invoiceAddress]);

  const renderView = observer(() => (
    <PaymentDataAddressView address={addressData} equal={isEqual} />
  ));

  const renderEdit = () => {
    return (
      <PaymentDataAddressForm
        initialValues={{ address: addressData, equal: isEqual }}
        formRef={form.formRef}
        onStateChanged={setCanSubmit}
        onSubmit={async (values) => {
          try {
            let data = undefined;
            if (values.equal) {
              data = {
                street_name: '',
                street_number: '',
                zip_code: '',
                address_supplement: '',
                city: '',
                province: '',
                country: ''
              };
            } else {
              data = values.address;
            }
            await companyStore.updatePaymentData({ invoice_address: data });
          } catch (error) {
            console.error(error);
          }
        }}
        useCustomButton
      />
    );
  };

  useEffect(() => {
    const abortController = new AbortController();
    const fetch = async () => {
      try {
        if (companyStore.paymentData) return;
        await companyStore.fetchPaymentData();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };
    fetch();

    return () => abortController.abort();
  }, []);

  return (
    <DataViewEditBox
      title={t('company:PAYMENT', { context: 'address' })}
      view={renderView}
      edit={renderEdit}
      canEdit
      canSubmit={canSubmit}
      onSubmit={form.handleSubmit}
      onCancel={form.handleReset}
    />
  );
});
